import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import PageContainer from '@src/components/Layout/PageContainer'
import PageHeader from '@src/components/Layout/PageHeader'
import PageLoader from '@src/components/Loader/PageLoader'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useClassificationReport } from '@src/api/report-api'
import { ClassificationReportResponse } from '@src/types/classificationReport'
import { ClassifiedDocumentType } from '@src/types/constants'
import ConfidenceIntervalsTable from './components/ConfidenceIntervalsTable'
import ReportFilters from './components/ReportFilters'
import ReportSummary from './components/ReportSummary'

const ClassificationReport = () => {
  const { t } = useTranslation()
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [productLine, setProductLine] = useState<string>('')
  const [classifiedDocumentType, setClassifiedDocumentType] = useState<ClassifiedDocumentType | undefined>()
  const [reportData, setReportData] = useState<ClassificationReportResponse | null>(null)

  const { mutateAsync: fetchReport, isPending } = useClassificationReport()

  const llmReport = reportData?.reports.find((r) => r.modelName === 'LLM')

  const handleGenerateReport = async () => {
    try {
      const result = await fetchReport({
        startDate: startDate ? startDate.toISOString().split('T')[0] : undefined,
        endDate: endDate ? endDate.toISOString().split('T')[0] : undefined,
        productLine: productLine || undefined,
        classifiedDocumentType: classifiedDocumentType,
      })
      setReportData(result)
    } catch (error) {
      console.error('Error fetching report:', error)
    }
  }

  const handleReset = () => {
    setStartDate(null)
    setEndDate(null)
    setProductLine('')
    setClassifiedDocumentType(undefined)
    setReportData(null)
  }

  useEffect(() => {
    void handleGenerateReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <PageHeader
        breadcrumbs={{
          path: [{ label: 'Documents', href: '/' }],
          current: t('reports.classificationReport'),
        }}
      />

      <PageContainer>
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          productLine={productLine}
          classifiedDocumentType={classifiedDocumentType}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onProductLineChange={setProductLine}
          onClassifiedDocumentTypeChange={setClassifiedDocumentType}
          onGenerate={handleGenerateReport}
          onReset={handleReset}
          isPending={isPending}
        />

        {isPending && <PageLoader />}

        {/* Report Results */}
        {reportData && !isPending && (
          <Box>
            <ReportSummary reportData={reportData} />

            {/* LLM Model Report */}
            {llmReport && (
              <Paper sx={{ p: 3, mb: 3 }}>
                {/* Metrics */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    {t('reports.overallMetrics')} ({llmReport.totalPages} pages)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('reports.truePositiveRate')}</TableCell>
                          <TableCell>{t('reports.falsePositiveRate')}</TableCell>
                          <TableCell>{t('reports.falseNegativeRate')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Typography sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                              {llmReport.metrics.truePositiveRate.toFixed(2)}%
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                              {llmReport.metrics.falsePositiveRate.toFixed(2)}%
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                              {llmReport.metrics.falseNegativeRate.toFixed(2)}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <ConfidenceIntervalsTable reports={[llmReport]} />
              </Paper>
            )}

            <Typography variant="caption" color="textSecondary">
              {t('reports.generatedAt')}: {new Date(reportData.generatedAt).toLocaleString()}
            </Typography>
          </Box>
        )}
      </PageContainer>
    </>
  )
}

export default ClassificationReport
